'use client';
import React from 'react';
import { CalendarClock, History, Lightbulb } from 'lucide-react';
import { useCertificates } from '@/contexts/CertificatesContext';
import { computeCertificateInsights } from '@/lib/certificate-alerts';

function dayText(days: number): string {
  const count = Math.abs(days);
  return `${count} day${count !== 1 ? 's' : ''}`;
}

export default function CertificateInsights() {
  const { certificates } = useCertificates();
  const { oldestExpired, nextUpcomingExpiry } = computeCertificateInsights(certificates);

  return (
    <div className="card-elevated p-4 xl:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={16} className="text-primary" />
        <h3 className="text-sm font-700 text-foreground">Certificate Insights</h3>
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-2.5">
          <History size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--status-critical)' }} />
          <div className="min-w-0">
            <p className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Oldest Expired Certificate</p>
            {oldestExpired ? (
              <>
                <p className="text-xs font-600 text-foreground mt-0.5 break-words">{oldestExpired.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Expired {dayText(oldestExpired.daysRemaining!)} ago</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">No expired certificates detected.</p>
            )}
          </div>
        </div>

        <div className="flex items-start gap-2.5 border-t border-border pt-3">
          <CalendarClock size={15} className="mt-0.5 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Next Upcoming Expiry</p>
            {nextUpcomingExpiry ? (
              <>
                <p className="text-xs font-600 text-foreground mt-0.5 break-words">{nextUpcomingExpiry.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {nextUpcomingExpiry.daysRemaining === 0
                    ? 'Expires today'
                    : `Expires in ${dayText(nextUpcomingExpiry.daysRemaining!)}`}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">No upcoming dated expiry found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
